export interface LatLng {
  lat: number;
  lng: number;
}

export interface RootStore {
  location: LatLng | null;
  city: string;
  hospitals: Hospital[];
  age: string;
  gender: string;
  state: number;
  covidState: number;
  needs: SpecialCareValue;
  reporter: number;
  waitTime: ConfidentValue;
  covidBedsReport: ConfidentValue;
  icuBedsReport: ConfidentValue;
  availableVentilatorsReport: ConfidentValue;
  totalVentilatorsReport: ConfidentValue;
  additionalCommentsReport: string;
  email: string;
  lastReportId: string;
  userName: string;
  hospitalsWithDetails: HospitalStatusDetails[];
  detailedHospitalsLastUpdate: Record<string, number>;
  careNeeds: string[];
}

export interface HospitalStatusDetails {
  hospital: Hospital;
  status: HospitalStatus;
}

export interface HospitalLocation {
  lat: number;
  lng: number;
  time_zone: string;
  address_line1: string;
  address_line2: string;
  city: string;
  county: string;
  state_province: string;
  postal_code: string;
  country: string;
}

export interface Hospital {
  id: string;
  name: string;
  location: HospitalLocation;
  travel_information?: TravelInfo;
  type: string;
  photo: string;

  // specialty: string[];
  // available_beds: number;
  // available_ventilators: number;
  // available_icu_beds: number;
  // available_corona_beds: number;
  // current_wait_time: number;
  // total_beds: number;
  // total_corona_beds: number;
  // total_icu_beds: number;
  // total_ventilators: number;
  // testing_available: number;
  // current_number_corona_patients_without_ventilator: number;
  // notes: string[];
}

export interface HospitalMatch extends Hospital {
  status: HospitalStatus;
}

export interface AggregateCounts {
  no_data: number;
  at_capacity: number;
  near_capacity: number;
  over_capacity: number;
  under_capacity: number;
}

export enum CapacityCategories {
  NO_DATA = 'NO_DATA',
  UNDER_CAPACITY = 'UNDER_CAPACITY',
  NEAR_CAPACITY = 'NEAR_CAPACITY',
  AT_CAPACITY = 'AT_CAPACITY',
  OVER_CAPACITY = 'OVER_CAPACITY',
}

// TODO(jjandoc): This reflects a new naming of capacity categories used by the
// simple hospital status report endpoint. Meanwhile, the existing endpoints
// still use the lower-case key version of these capacity categories for things
// like aggregate hospital data. This is going to be confusing and we probably
// need to revisit how this is used soon to unify under one naming scheme.
export enum CapacityType {
  NO_DATA = 'UNSPECIFIED',
  UNDER_CAPACITY = 'HEALTHY_CAPACITY',
  NEAR_CAPACITY = 'LIMITED_CAPACITY',
  AT_CAPACITY = 'BORDERLINE_NO_CAPACITY',
  OVER_CAPACITY = 'NO_CAPACITY',
}

export enum AggregateChartColors {
  NO_DATA = '#C4BECD',
  UNDER_CAPACITY = '#357CEB',
  NEAR_CAPACITY = '#F7CF4F',
  AT_CAPACITY = '#F19938',
  OVER_CAPACITY = '#F15938',
}

export enum CapacityCategoryLabels {
  NO_DATA = 'No data',
  UNDER_CAPACITY = 'Healthy availability',
  NEAR_CAPACITY = 'Limited availability',
  AT_CAPACITY = 'Borderline no availability',
  OVER_CAPACITY = 'No availability',
}

export interface HospitalAggregate {
  id: string;
  name: string;
  level: AggregateGranularity;
  location: LatLng;
  reporting_hospitals: number;
  total_hospitals: number;
  beds: AggregateCounts;
  corona_beds: AggregateCounts;
  ecmo: AggregateCounts;
  icu_beds: AggregateCounts;
  ventilators: AggregateCounts;
  average_wait_time: number;
}

export enum HospitalResources {
  BEDS = 'Bed',
  CORONA_BEDS = 'COVID-19 Bed',
  ICU_BEDS = 'ICU Bed',
  ECMO = 'ECMO Machine',
  VENTILATORS = 'Ventilator',
}

export enum CareNeeds {
  ICU = 'ICU Beds',
  VENTILATOR = 'Ventilators',
  COVID_TEST = 'COVID-19 Testing',
  NON_COVID_CARE = 'Non COVID-19 Care',
}

export enum AggregateGranularity {
  STATE = 'state',
  COUNTY = 'county',
  CITY = 'city',
  HOSPITAL = 'hospital',
}

export const MockHospitalAggregate: HospitalAggregate = {
  id: '123',
  name: 'California',
  level: AggregateGranularity.STATE,
  location: {
    lat: 38.5816,
    lng: -121.4944,
  },
  reporting_hospitals: 4,
  total_hospitals: 9,
  beds: {
    no_data: 50,
    at_capacity: 25,
    near_capacity: 5,
    over_capacity: 10,
    under_capacity: 15,
  },
  corona_beds: {
    no_data: 30,
    at_capacity: 45,
    near_capacity: 5,
    over_capacity: 10,
    under_capacity: 15,
  },
  icu_beds: {
    no_data: 20,
    at_capacity: 35,
    near_capacity: 5,
    over_capacity: 15,
    under_capacity: 25,
  },
  ecmo: {
    no_data: 5,
    at_capacity: 2,
    near_capacity: 5,
    over_capacity: 3,
    under_capacity: 4,
  },
  ventilators: {
    no_data: 5,
    at_capacity: 2,
    near_capacity: 5,
    over_capacity: 3,
    under_capacity: 4,
  },
  average_wait_time: 60,
};

export const MockHospitalAggregate2: HospitalAggregate = {
  id: '456',
  name: 'New York',
  level: AggregateGranularity.STATE,
  location: {
    lat: 42.6526,
    lng: -73.7562,
  },
  reporting_hospitals: 8,
  total_hospitals: 15,
  beds: {
    no_data: 50,
    at_capacity: 25,
    near_capacity: 5,
    over_capacity: 10,
    under_capacity: 15,
  },
  corona_beds: {
    no_data: 30,
    at_capacity: 45,
    near_capacity: 5,
    over_capacity: 10,
    under_capacity: 15,
  },
  icu_beds: {
    no_data: 20,
    at_capacity: 35,
    near_capacity: 5,
    over_capacity: 15,
    under_capacity: 25,
  },
  ecmo: {
    no_data: 5,
    at_capacity: 2,
    near_capacity: 5,
    over_capacity: 3,
    under_capacity: 4,
  },
  ventilators: {
    no_data: 5,
    at_capacity: 2,
    near_capacity: 5,
    over_capacity: 3,
    under_capacity: 4,
  },
  average_wait_time: 60,
};

export interface TravelInfo {
  // meters
  travel_distance: number;
  // seconds
  travel_time: number;
}

export interface LocationValue {
  formatted_address: string;
  name: string;
}
export interface SpecialCareValue {
  icu: boolean;
  ventilator: boolean;
}

export enum PatientState {
  NONE = 'NONE',
  MILD = 'MILD',
  SEVERE = 'SEVERE',
  CRITICAL = 'CRITICAL',
}
export enum State {
  NONE,
  MILD,
  SEVERE,
  CRITICAL,
}
export enum CovidState {
  NONE = 'NONE',
  LIKELY = 'LIKELY',
  TESTED = 'TESTED',
  CONFIRMED = 'CONFIRMED',
}

export enum PatientIntent {
  EXPLORATION = 'EXPLORATION',
  NEEDHELP = 'NEEDHELP',
}

export enum ReporterType {
  UNSPECIFIED = 'UNSPECIFIED',
  PATIENT = 'PATIENT',
  MEDICAL_WORKER = 'MEDICAL_WORKER',
  STAFF = 'STAFF',
  OFFICIAL = 'OFFICIAL',
}

export enum ConfidenceLevel {
  UNSPECIFIED = 'UNSPECIFIED',
  GUESS = 'GUESS',
  EDUCATED_ESTIMATION = 'EDUCATED_ESTIMATION',
  CERTAIN = 'CERTAIN',
}

export interface HospitalReport {
  hospital_id: string;
  description?: string;
  beds?: Capacity;
  corona_beds?: Capacity;
  icu_beds?: Capacity;
  adult_ventilators?: Capacity;
  ped_ventilators?: Capacity;
  current_wait_time_seconds?: ConfidenceValue;
}

export interface SimpleHospitalReport {
  id: string;
  filed_at: number;
  reporter_type: ReporterType;
  capacity_type: CapacityType;
}

export interface ReportAttribution {
  report_id: string;
  email: string;
  reporter_type: ReporterType;
}

// export const MockHospitalReport: HospitalReport = {
//   id: 'a1111c8c-c720-46c3-8534-2fcdd730040d',
//   reporter_type: ReporterType.PATIENT,
//   email: 'patient@patient.com',
//   available_beds: 10000,
//   filed_at: 0,
//   report_id: '',
// };

// export const MockHospital: Hospital = {
//   id: 'a1111c8c-c720-46c3-8534-2fcdd7300403',
//   name: 'Brigham and Womens Hospital',
//   //phone: "123-456-7890",
//   //type: "Short Term Acute Care Hospital",
//   notes: ['static data'],
//   location: {
//     lat: 42.3362036,
//     lng: -71.1067408,
//     time_zone: 'America/New_York',
//     address_line1: '75 Francis St',
//     address_line2: '',
//     //county: "Suffolk",
//     city: 'Boston',
//     state_province: 'Massachusetts',
//     postal_code: '02115',
//     country: 'USA',
//     county: '',
//   },
//   photo:
//     'https://lh5.googleusercontent.com/p/AF1QipMrIMd87h5JW9XmlXFv-aiCLKiZBJaJd-FG1RmV=w408-h273-k-no',
//   specialty: [''],
//   available_beds: 763,
//   available_corona_beds: 260,
//   available_icu_beds: 15,
//   available_ventilators: 5,
//   total_ventilators: 15,
//   current_number_corona_patients_without_ventilator: 10,
//   current_wait_time: 1800,
// };

export interface PatientDetails {
  intent: PatientIntent;
  patient_status: PatientState;
  covid19_status: CovidState;
  current_location: HospitalLocation;
  filed_at: number;
  immediate_medical_needs: string[];
  patient_prognosis: string[];
  patient_demographics: string[];
  preferred_hospitals: string[];
  preferred_regions: string[];
  preferred_types: string[];
}

export interface ConfidentValue {
  value: number | '';
  confidence: number;
}

export interface ConfidenceValue {
  value: number;
  confidence: ConfidenceLevel;
}

export interface Capacity {
  available?: ConfidenceValue;
  used?: ConfidenceValue;
  total?: ConfidenceValue;
}

// For v2 of the API.
export interface HospitalV2 {
  id: string;
  name: string;
  location: HospitalLocation;
  travel_information?: TravelInfo;
  type: string;
  phone: string;
  photo: string;
  url: string;
}

export interface ItemStatus {
  available: ConfidenceValue;
  used: ConfidenceValue;
  total: ConfidenceValue;
}

// TODO(jjandoc): This is still subject to change!!!
export interface HospitalStatus {
  created_at: string;
  updated_at: string;
  hospital: string;
  beds: ItemStatus;
  icu_beds: ItemStatus;
  adult_ventilators: ItemStatus;
  // available_corona_beds_confidence: string;
  // available_corona_beds_value: number;
  // available_icu_beds_confidence: string;
  // available_icu_beds_value: number;
  // available_ventilators_confidence: string;
  // available_ventilators_value: number;
  // current_wait_time_minutes_confidence: string;
  current_wait_time_seconds: ConfidenceValue;
  source: string;
}

export const MockHospitalStatus: HospitalStatus = {
  created_at: '2011-10-05T14:48:00.000Z',
  updated_at: '2020-10-05T14:48:00.000Z',
  hospital: '123',
  beds: {
    available: {
      value: 80,
      confidence: ConfidenceLevel.UNSPECIFIED,
    },
    used: {
      value: 20,
      confidence: ConfidenceLevel.UNSPECIFIED,
    },
    total: {
      value: 100,
      confidence: ConfidenceLevel.UNSPECIFIED,
    },
  },
  icu_beds: {
    available: {
      value: 5,
      confidence: ConfidenceLevel.GUESS,
    },
    used: {
      value: 95,
      confidence: ConfidenceLevel.GUESS,
    },
    total: {
      value: 100,
      confidence: ConfidenceLevel.GUESS,
    },
  },
  adult_ventilators: {
    available: {
      value: 0,
      confidence: ConfidenceLevel.EDUCATED_ESTIMATION,
    },
    used: {
      value: 150,
      confidence: ConfidenceLevel.EDUCATED_ESTIMATION,
    },
    total: {
      value: 100,
      confidence: ConfidenceLevel.EDUCATED_ESTIMATION,
    },
  },
  current_wait_time_seconds: {
    value: 3600,
    confidence: ConfidenceLevel.CERTAIN,
  },
  source: '5 reports',
};

export enum TargetAudiences {
  DOCTOR = 'DOCTOR',
  EMT = 'EMT',
  PATIENT = 'PATIENT',
  REPORTING = 'REPORTING',
  DEFAULT = 'DEFAULT',
}
